import {
  DEFAULT_EVALUATION_MAX_CONCURRENCY,
  getEvaluationMaxJobConcurrency,
  MAX_CONCURRENCY,
} from "@/constants/evaluation-concurrency"
import {
  isAutomaticExpectedBehaviorMetric,
  isExpectedBehaviorCompositeMetric,
} from "@/metrics/registry"

import type { OrganizationRecord } from "@/types/api/OrganizationRecord"
import type { TestSetRecord } from "@/types/api/TestSetRecord"
import { isJsonObjectValue, type JsonObject } from "@/types/json"

import { UNRESOLVED_METRIC_SELECTION_MESSAGE } from "./selectedMetricIds"
import type {
  SimulationLaunchMetricRecordsByName,
  SimulationTemplateData,
} from "./types"

export const DEFAULT_CONCURRENCY = 5

interface EvaluationSummaryData {
  metricsCount: number
  iterations: number
  concurrency: number
  totalScenarios: number | null
}

type EvaluationLauncherOrganizationRecord = Pick<
  OrganizationRecord,
  "coval_integration" | "resolved_max_job_concurrency"
>

export function getEvaluationLauncherMaxConcurrency(
  organizationRecord: EvaluationLauncherOrganizationRecord | null,
): number {
  if (!organizationRecord) {
    return DEFAULT_EVALUATION_MAX_CONCURRENCY
  }

  // Prefer the backend-resolved value, clamped to the frontend upper bound;
  // else fall back to coval_integration.max_job_concurrency.
  const resolved = organizationRecord.resolved_max_job_concurrency
  if (
    typeof resolved === "number" &&
    Number.isFinite(resolved) &&
    resolved > 0
  ) {
    return Math.min(Math.trunc(resolved), MAX_CONCURRENCY)
  }

  return getEvaluationMaxJobConcurrency(organizationRecord.coval_integration)
}

export interface LaunchValidationError {
  message: string
  // Persistent errors (e.g. a plan limit) are explained on the launch card as
  // soon as they apply, so the user knows why Launch is disabled. Transient ones
  // (missing selections) surface only once a launch is attempted.
  persistent?: boolean
}

interface ParsedEvaluationRunMetadata {
  customerMetadata?: JsonObject
  error: string | null
}

export function parseEvaluationRunMetadata(
  value: string,
): ParsedEvaluationRunMetadata {
  if (value.trim() === "") {
    return { customerMetadata: undefined, error: null }
  }

  try {
    const parsed: unknown = JSON.parse(value)
    if (!isJsonObjectValue(parsed)) {
      return {
        customerMetadata: undefined,
        error: "Run metadata must be a JSON object",
      }
    }

    return { customerMetadata: parsed, error: null }
  } catch {
    return {
      customerMetadata: undefined,
      error: "Run metadata must be valid JSON",
    }
  }
}

export function getEvaluationValidationErrors(
  templateData: SimulationTemplateData,
  metricsPerCallLimit: number | null = null,
  resolvedMetricsCount: number | null = null,
): LaunchValidationError[] {
  const errors: LaunchValidationError[] = []

  if (!templateData.agentIds || templateData.agentIds.length === 0) {
    errors.push({ message: "Select at least one Agent" })
  }

  if (!templateData.testSetIds || templateData.testSetIds.length === 0) {
    errors.push({ message: "Select a Test Set" })
  }

  if ((templateData.selectedTestCaseIds?.length ?? 0) > 100) {
    errors.push({ message: "Select no more than 100 test cases" })
  }

  const metricsCount = templateData.enabledMetrics.length
  const submittedMetricsCount = resolvedMetricsCount ?? metricsCount
  if (!metricsCount && !templateData.hasAutomaticMetrics) {
    errors.push({ message: "Select at least one Metric" })
  } else if (
    metricsPerCallLimit !== null &&
    submittedMetricsCount > metricsPerCallLimit
  ) {
    errors.push({
      message: `This run has ${submittedMetricsCount} Metrics; your plan allows ${metricsPerCallLimit} per call. Remove Metrics or upgrade your plan.`,
      persistent: true,
    })
  }

  if (submittedMetricsCount < metricsCount) {
    errors.push({
      message: UNRESOLVED_METRIC_SELECTION_MESSAGE,
      persistent: true,
    })
  }

  if (!templateData.personaIds || templateData.personaIds.length === 0) {
    errors.push({
      message: "Select at least one Persona",
    })
  }

  return errors
}

export function getAdjustedEvaluationConcurrency(
  testSetRecords: TestSetRecord[],
  testSetId: string,
  iterationCount: number,
  currentConcurrency: number,
): number {
  const selectedTestSet = testSetRecords.find(
    (testSet) => testSet.id === testSetId,
  )
  if (!selectedTestSet) {
    return currentConcurrency
  }

  const totalIterations =
    (selectedTestSet.test_case_count ?? 0) * iterationCount
  if (currentConcurrency > totalIterations) {
    return Math.max(0, totalIterations)
  }

  return currentConcurrency
}

/**
 * Selected test case IDs only make sense for the single test set they were
 * picked from, so a change to the test set selection drops them unless that
 * same test set is still the only one selected.
 */
export function getRetainedSelectedTestCaseIds(
  previousTestSetIds: string[] | undefined,
  nextTestSetIds: string[],
  selectedTestCaseIds: string[] | undefined,
): string[] | undefined {
  if (!selectedTestCaseIds?.length) {
    return undefined
  }

  if (nextTestSetIds.length !== 1 || previousTestSetIds?.length !== 1) {
    return undefined
  }

  return nextTestSetIds[0] === previousTestSetIds[0]
    ? selectedTestCaseIds
    : undefined
}

export function hasSelectedTestSetWithExpectedBehaviors(
  testSetRecords: TestSetRecord[],
  selectedTestSetIds: string[],
): boolean {
  const selectedIds = new Set(selectedTestSetIds)

  return testSetRecords.some(
    (testSet) =>
      selectedIds.has(testSet.id) && testSet.has_expected_behaviors === true,
  )
}

export function getExpectedBehaviorCompositeMetricName(
  metricRecords: SimulationLaunchMetricRecordsByName,
): string | undefined {
  return Object.entries(metricRecords)
    .filter(
      ([, metric]) =>
        Boolean(metric.id) &&
        !isAutomaticExpectedBehaviorMetric(metric) &&
        isExpectedBehaviorCompositeMetric(metric),
    )
    .sort(([leftName, left], [rightName, right]) => {
      const displayNameComparison = (
        left.metric_display_name ?? leftName
      ).localeCompare(right.metric_display_name ?? rightName)

      return displayNameComparison || leftName.localeCompare(rightName)
    })[0]?.[0]
}

export function buildEvaluationSummaryData(
  templateData: SimulationTemplateData,
  testSetRecords: TestSetRecord[],
): EvaluationSummaryData {
  const testSetIds = templateData.testSetIds || []
  const firstTestSet = testSetRecords.find(
    (testSet) => testSet.id === testSetIds[0],
  )

  const iterations = templateData.iterationCount || 1
  const selectedCount = templateData.selectedTestCaseIds?.length || 0

  let totalCaseCount = 0
  let totalScenarios: number | null = null

  if (testSetIds.length > 0) {
    if (selectedCount > 0) {
      totalCaseCount = selectedCount
    } else if (testSetIds.length === 1) {
      const count = firstTestSet?.test_case_count || 0
      const effectiveCount =
        templateData.shouldSubSample && templateData.subSampleSize
          ? Math.min(templateData.subSampleSize, count)
          : count
      totalCaseCount = effectiveCount
    } else {
      for (const id of testSetIds) {
        const testSet = testSetRecords.find((ts) => ts.id === id)
        const count = testSet?.test_case_count || 0
        const perSetCount =
          templateData.shouldSubSample && templateData.subSampleSize
            ? Math.min(templateData.subSampleSize, count)
            : count
        totalCaseCount += perSetCount
      }
    }
    totalScenarios = totalCaseCount > 0 ? totalCaseCount * iterations : null
  }

  return {
    metricsCount: templateData.enabledMetrics.length || 0,
    iterations,
    concurrency: templateData.concurrency || 1,
    totalScenarios,
  }
}

export function isMultiRunEvaluation(
  templateData: SimulationTemplateData,
): boolean {
  return (
    (templateData.agentIds?.length || 0) > 1 ||
    (templateData.personaIds?.length || 0) > 1 ||
    (templateData.testSetIds?.length || 0) > 1
  )
}

export function hasMultipleEvaluationPersonas(
  templateData: SimulationTemplateData,
): boolean {
  return (templateData.personaIds?.length || 0) > 1
}

export function hasMultipleEvaluationTestSets(
  templateData: SimulationTemplateData,
): boolean {
  return (templateData.testSetIds?.length || 0) > 1
}
